import GdsClient, { EventBody, EventResponse, Question } from '../../gds-api-client';
import RightRail, { Route, RouterProvider } from '../../gs-component-right-rail';
import { Formik, FormikHelpers as FormikActions, FormikProps } from 'formik';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import React, { useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';

import { ApiClientsContext, EventType } from '../components/ApiClientsContext';
import { DeleteConfirmation } from '../components/DeleteConfirmation';
import { EditConfirmation } from '../components/EditConfirmation';
import { ErrorContext } from '../components/ErrorContext';
import { LocationContext } from '../components/LocationContext';
import { MemberContext } from '../components/MemberContext';
import { TrackingContext } from '../components/TrackingContext';
import { bookingEntryAddressParser, ParsedBooking } from '../helpers/addressComponentHelper';
import { getBookingBody } from '../helpers/getBookingBody';
import { getLocation, getLegacyLocation } from '../helpers/getLocation';
import addMealQuestionToQuestions from '../helpers/addMealQuestionToQuestions';
import { AddCustomVendor } from './AddCustomVendor';
import { EventLocation, LegacyEvent } from './EventForm';
import { RightRailContent } from './RightRailContent';
import { SubEventScreen } from './SubEventScreen';
import { emptyMealQuestion } from '../ScheduleOfEvents/helpers';

export interface InitialValues {
  name: string | null;
  attire?: string | null;
  notes?: string | null;
  date?: string | null;
  time?: string | null;
  endTime?: string | null;
  visible?: boolean;
  requireRsvp?: boolean;
  sameVenue?: boolean;
  location: EventLocation;
  hideDate?: boolean | null;
  mealQuestion?: Question | null;
}

export interface FormValues {
  events: InitialValues[];
}

export interface DeletedResponse {
  deleted: boolean;
  id: string;
}

export type OnSuccess = (event: EventResponse | DeletedResponse) => void;

export interface EventModalProps {
  open: boolean;
  setClose: (value: boolean) => void;
  weddingId: string;
  type: EventType;
  event: LegacyEvent | LegacyEvent[] | null;
  hasWWS: boolean;
  hideDate?: boolean | null;
  onSuccess: OnSuccess;
  onHideDate: (hideDate: boolean) => void;
}

const validationSchema = Yup.object().shape({
  events: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Event Name is required'),
    })
  ),
});

const setInitialEventsValue = (
  // @ts-ignore
  setInitialValues,
  // @ts-ignore
  event,
  // @ts-ignore
  eventLocation,
  // @ts-ignore
  hideDate?,
  // @ts-ignore
  receptionEvent?
): void => {
  const {
    name = '',
    attire = '',
    date = '',
    time = '',
    endTime = '',
    requireRsvp = false,
    visible = true,
    notes = '',
    sameVenue = false,
    questions = [],
  } = event;
  let mealQuestion;
  if (questions) {
    // @ts-ignore
    mealQuestion = questions.find(question => question.type === 'Meal') || emptyMealQuestion();
  } else {
    mealQuestion = emptyMealQuestion();
  }

  const events = [
    {
      name: name || '',
      attire: attire || '',
      date: date || '',
      time: time || '',
      endTime: endTime || '',
      notes: notes || '',
      visible: visible === null ? true : visible,
      requireRsvp: requireRsvp || false,
      mealQuestion,
      hideDate: Boolean(hideDate),
      sameVenue,
      location: eventLocation,
    },
  ];

  if (receptionEvent) events.push(receptionEvent);

  setInitialValues({
    events,
  });
};
// @ts-ignore
const setFormikData = (gdsEvent, setInitialValues): void => {
  const { location = {} } = gdsEvent;
  const eventLocation = getLocation(location);

  setInitialEventsValue(setInitialValues, gdsEvent, eventLocation);
};

const setFormikDataForLegacyEvents = (
  // @ts-ignore
  gdsEvent,
  // @ts-ignore
  locationStore,
  // @ts-ignore
  setInitialValues,
  // @ts-ignore
  hideDate
): void => {
  // Ceremony and Reception initial values
  let ceremony: ParsedBooking | undefined;
  let reception: ParsedBooking | undefined;
  const { name: receptionEventName, sameVenue: receptionSameVenue } = gdsEvent[1];

  if (locationStore.CER) {
    ceremony = bookingEntryAddressParser(locationStore.CER);
  }
  if (locationStore.REC) {
    reception = bookingEntryAddressParser(locationStore.REC);
  }

  const location = getLegacyLocation(ceremony);
  const receptionLocation = getLegacyLocation(reception);
  const receptionEvent = {
    name: receptionEventName || '',
    sameVenue: receptionSameVenue,
    location: receptionLocation,
  };

  setInitialEventsValue(setInitialValues, gdsEvent[0], location, hideDate, receptionEvent);
};

const client = new GdsClient();

export const RightRailForm: React.FC<EventModalProps> = ({
  open,
  setClose,
  weddingId,
  type,
  event: gdsEvent,
  hasWWS,
  hideDate,
  onSuccess,
  onHideDate,
}) => {
  const initial: FormValues = {
    events: [
      {
        name: '',
        attire: '',
        date: '',
        time: '',
        endTime: '',
        notes: '',
        visible: true,
        requireRsvp: false,
        mealQuestion: emptyMealQuestion(),
        sameVenue: false,
        hideDate: Boolean(hideDate),
        location: {
          name: '',
          street1: '',
          city: '',
          state: '',
          zip: '',
          country: '',
        },
      },
    ],
  };

  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(initial);
  const { location: locationStore, locationsSaved, setLocationsSaved } = useContext(LocationContext);
  const { setErrors, clearError } = useContext(ErrorContext);
  const { member: { id: memberId = '' } = {} } = useContext(MemberContext);
  const { weddingApi, bookingsSource = '', setEventType, setGdsEvent, memberWeddingApi } = useContext(
    ApiClientsContext
  );
  const { track } = useContext(TrackingContext);

  useEffect(() => {
    if (gdsEvent && !Array.isArray(gdsEvent)) {
      setEventType('WeddingEvent');
      setGdsEvent(gdsEvent);

      setFormikData(gdsEvent, setInitialValues);
    } else if (Array.isArray(gdsEvent)) {
      setEventType('Ceremony');
      setGdsEvent(gdsEvent);

      setFormikDataForLegacyEvents(gdsEvent, locationStore, setInitialValues, hideDate);
    } else {
      setEventType('WeddingEvent');
      setInitialValues(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gdsEvent, hideDate]);

  const isNew = !gdsEvent;

  // @ts-ignore
  const onSuccessfulSave = (data, resetForm): void => {
    const successData = {
      ...data,
      location: data.location || null,
    };
    onSuccess(successData);
    setLoading(false);
    clearError();
    resetForm();
    setClose(false);
  };

  const onSubmit = async (values: FormValues, { resetForm }: FormikActions<FormValues>): Promise<void> => {
    setLoading(true);
    try {
      const { location, hideDate: hideDateValue, mealQuestion, ...rest } = values.events[0];
      const questions = Array.isArray(gdsEvent) ? get(gdsEvent[0], ['questions']) : get(gdsEvent, ['questions']);

      const body = {
        type: type || 'WeddingEvent',
        location: getLocation(location),
        weddingId,
        // @ts-ignore
        questions: addMealQuestionToQuestions(questions, mealQuestion),
        ...rest,
      };

      if (isNew) {
        // Creating a new event;
        if (!gdsEvent && !Array.isArray(gdsEvent)) {
          // Regular Events
          const { data } = await client.createEvent(weddingId, body);

          track('Event Added', {
            source: 'event modal',
            eventName: data.name,
            eventType: data.type === 'WeddingEvent' ? 'custom' : 'default',
            eventVisible: data.visible,
            rsvpOn: data.requireRsvp,
          });
          onSuccessfulSave(data, resetForm);
        }
        // Updating an event
      } else if (Array.isArray(gdsEvent)) {
        // Wedding & Reception Event
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { date, location: bodyLocation, ...restBody } = body;
        if (date) {
          // Wedding Date to Wedding API
          await weddingApi.upsertWedding({
            wedding: {
              member_id: memberId,
              wedding_date: date,
            },
          });
        }

        // Location to Bookings API
        if (locationStore.CER && !isEmpty(locationStore.CER.address_components)) {
          const { booking: upsertedBooking } = await weddingApi.upsertBooking({
            booking: getBookingBody({
              ...locationStore.CER,
              category_code: 'CER',
              memberId,
              bookingsSource,
            }),
          });

          if (!locationsSaved.CER) {
            // Send a "booking-created" analytics event
            track('Booking Created', {
              source: 'event-modal',
              booking_id: upsertedBooking.id,
              wedding_id: weddingId,
              member_id: memberId,
              event_id: gdsEvent[0].id,
            });
          }

          setLocationsSaved({
            CER: true,
            REC: locationsSaved.REC,
          });
        }

        if (locationStore.REC && !isEmpty(locationStore.REC.address_components)) {
          const { booking: upsertedBooking } = await weddingApi.upsertBooking({
            booking: getBookingBody({
              ...locationStore.REC,
              memberId,
              category_code: 'REC',
              bookingsSource,
            }),
          });

          if (!locationsSaved.REC) {
            // Send a "booking-created" analytics event
            track('Booking Created', {
              source: 'event-modal',
              booking_id: upsertedBooking.id,
              wedding_id: weddingId,
              member_id: memberId,
              event_id: gdsEvent[0].id,
            });
          }

          setLocationsSaved({
            CER: locationsSaved.CER,
            REC: true,
          });
        }

        // Save hide date perference
        const {
          data: {
            data: { updateWeddingWebsite },
          },
        } = await memberWeddingApi.updateMemberWedding({ hideDate: hideDateValue as boolean });
        onHideDate(updateWeddingWebsite.hideDate);

        const cerBody: EventBody = restBody;
        if (gdsEvent[0].image) {
          cerBody.image = gdsEvent[0].image;
        }

        const { data: cerRes } = await client.putEvent(gdsEvent[0].id, cerBody);

        // For Ceremony Callback
        onSuccess({ id: gdsEvent[0].id, ...body });

        // For Reception Callback
        const { location: recLocation }: any = values.events[1];
        const { name: recName, id: recId, type: recType, weddingId: recWeddingId, createdAt, updatedAt } = gdsEvent[1];

        track('Event Updated', {
          source: 'event modal',
          eventName: cerRes.name,
          eventType: cerRes.type === 'WeddingEvent' ? 'custom' : 'default',
          eventVisible: cerRes.visible,
          rsvpOn: cerRes.requireRsvp,
        });
        onSuccessfulSave(
          {
            id: recId,
            name: recName,
            location: recLocation,
            type: recType,
            weddingId: recWeddingId,
            createdAt,
            updatedAt,
          },
          resetForm
        );
      } else if (gdsEvent) {
        // Regular Events
        const customBody: EventBody = body;

        if (gdsEvent.image) {
          customBody.image = gdsEvent.image;
        }

        const { data } = await client.putEvent(gdsEvent.id, customBody);

        track('Event Updated', {
          source: 'event modal',
          eventName: data.name,
          eventType: data.type === 'WeddingEvent' ? 'custom' : 'default',
          eventVisible: data.visible,
          rsvpOn: data.requireRsvp,
        });
        onSuccessfulSave(data, resetForm);
      }
      // @ts-ignore
    } catch ({ response }) {
      setLoading(false);
      if (response?.data?.error === 'cannot update meals after guests have selected meals') {
        setErrors({ meals: "Meals can't be changed since guests have already started to RSVP." });
      } else {
        setErrors();
      }
    }
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {(formProps: FormikProps<FormValues>): JSX.Element => (
        <RightRail open={open}>
          <RouterProvider entry="main">
            <Route
              screen="main"
              component={
                <RightRailContent
                  hasWWS={hasWWS}
                  formProps={formProps}
                  isNew={isNew}
                  setClose={setClose}
                  loading={loading}
                />
              }
            />
            <Route screen="subEvent" component={<SubEventScreen formProps={formProps} />} />
            <Route
              screen="confirmation"
              component={
                <EditConfirmation
                  resetForm={formProps.resetForm}
                  submitForm={formProps.submitForm}
                  setClose={setClose}
                />
              }
            />
            <Route screen="addCustomVendor" component={<AddCustomVendor formProps={formProps} />} />
            <Route screen="delete" component={<DeleteConfirmation setClose={setClose} onSuccess={onSuccess} />} />
          </RouterProvider>
        </RightRail>
      )}
    </Formik>
  );
};
